import { useState } from "react";

import { PinModal } from "components";
import { QUESTION_RESULT_STATUSES } from "models";
import { TyreQuestionResults } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/components/TyreQuestionResults";
import "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/QuestionResults.scss";
import { useAppointmentDealer, useConnectedQuestionResultIdToFilter } from "modules/AppointmentDetails/hooks";
import { QuestionResult } from "modules/HoldingAppointmentDetails/components/Checks/components/Check/components/QuestionResults/components";

import type { PinModelWithHistory } from "components";
import type { Appointment } from "models";
import type { OrderedGroupedCheck, SortedQuestionResult } from "modules/AppointmentDetails/hooks";

type QuestionResultsProps = {
  appointment: Appointment;
  check: OrderedGroupedCheck;
  showAll?: boolean;
};

export const QuestionResults = ({ appointment, check, showAll }: QuestionResultsProps) => {
  const appointmentDealer = useAppointmentDealer(appointment);
  const [selectedPinWithHistory, setSelectedPinWithHistory] = useState<PinModelWithHistory | null>(null);

  const connectedQuestionResultId = useConnectedQuestionResultIdToFilter();

  const handleOpenPinModal = (pinWithHistory: PinModelWithHistory) => setSelectedPinWithHistory(pinWithHistory);

  const renderGroup = (groupOrder: number, questionResults: SortedQuestionResult[]) => {
    if (!questionResults.length) return;

    const visibleQuestions = questionResults.filter(questionResult => {
      if (!showAll && !questionResult.tyre && (questionResult.status === QUESTION_RESULT_STATUSES.OK || questionResult.status === QUESTION_RESULT_STATUSES.NVT)) {
        return false;
      }

      return !connectedQuestionResultId || connectedQuestionResultId === questionResult.id;
    });

    if (!visibleQuestions.length) return null;

    if (visibleQuestions.some(q => q.tyre_id))
      return (
        <div key={"question-group-" + groupOrder} className="QuestionResults-group">
          <TyreQuestionResults questionResults={visibleQuestions} template={check.checklist?.template} appointment={appointment} readOnly />
        </div>
      );

    return (
      <div key={"question-group-" + groupOrder} className="QuestionResults-group">
        <div className="QuestionResults-group_header">
          <p>{visibleQuestions[0].group_name}</p>
        </div>
        {visibleQuestions.map(questionResult => (
          <QuestionResult
            questionResult={questionResult}
            checklist={check.checklist}
            key={questionResult.id}
            appointment={appointment}
            onOpenPinModal={handleOpenPinModal}
            connectedQuestionResultId={connectedQuestionResultId}
          />
        ))}
      </div>
    );
  };

  const renderGroups = () => Array.from(check._groupedQuestionResults, ([groupOrder, questionResults]) => renderGroup(groupOrder, questionResults));

  return (
    <>
      <div className="QuestionResults">{renderGroups()}</div>
      {selectedPinWithHistory !== null && (
        <PinModal isOpen pin={selectedPinWithHistory} dealer={appointmentDealer} onClose={() => setSelectedPinWithHistory(null)} check={check} readOnly />
      )}
    </>
  );
};
